import type { LanguageProperty, ValidIconName } from 'ehs-react';
import { Button, Icon, intl, intlFmt, TextInput } from 'ehs-react';
import React, { useState } from 'react';
import { usePortletStore } from '../App';
import CommunicationUtil from '../util/CommunicationUtil';

function AuthenticationWizardSetPasswordPage({
  onNextPage
}: Readonly<{
  onNextPage: () => void;
}>): JSX.Element {
  const [passwordPolicy] = usePortletStore(state => [
    state.configuration.passwordPolicy
  ]);
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [hasMinLength, setHasMinLength] = useState('grey');
  const [hasMinNumbers, setHasMinNumbers] = useState('grey');
  const [hasMinLowerCase, setHasMinLowerCase] = useState('grey');
  const [hasMinUpperCase, setHasMinUpperCase] = useState('grey');
  const [hasMinSymbols, setHasMinSymbols] = useState('grey');
  const [samePasswords, setSamePasswords] = useState('grey');
  const [isRequestPending, setRequestPending] = useState(false);
  const [backendError, setBackendError] = useState('');
  const getIconName = (
    state: string
  ): { 'aria-label': string; iconName: ValidIconName } => {
    return state === 'red'
      ? { iconName: 'error', 'aria-label': intl('common.error') }
      : { iconName: 'ok', 'aria-label': intl('common.ok') };
  };

  const allPasswordRulesChecked = () => {
    return (
      (!passwordPolicy.minLength || hasMinLength === 'green') &&
      (!passwordPolicy.minNumbers || hasMinNumbers === 'green') &&
      (!passwordPolicy.minLowerCase || hasMinLowerCase === 'green') &&
      (!passwordPolicy.minUpperCase || hasMinUpperCase === 'green') &&
      (!passwordPolicy.minSymbols || hasMinSymbols === 'green') &&
      samePasswords === 'green'
    );
  };

  /**
   * Helper function to create the regex and count the number of defined characters.
   * @param str - input string
   * @param regex
   */
  function findTotalCount(str: string, regex: string) {
    const newRegex = new RegExp(regex, 'g');
    const digitsArr = str.match(newRegex);
    if (digitsArr) {
      return digitsArr.join('').length;
    }
    return 0;
  }

  /**
   * Function to check the number of characters in input string, defined by regex expression.
   * @param value - input string
   * @param regex - regex defining the characters to be checked
   * @param number - minimal required number of characters in input string
   * @param state - name of the state
   * @param setter - setter for the state
   */
  const checkNumberOfCharactersByRegex = (
    value: string,
    regex: string,
    number: number,
    state: string,
    setter: (newState: string) => void
  ) => {
    if (findTotalCount(value, regex) >= number) {
      if (state !== 'green') {
        setter('green');
      }
    } else {
      if (state !== 'red') {
        setter('red');
      }
    }
  };

  /**
   * Function to check the number of special characters in a string.
   * @param value - input string
   * @param characters - characters to be checked
   * @param number - minimal required number of characters in input string
   * @param state - name of the state
   * @param setter - setter for the state
   */
  const checkNumberOfSpecialCharacters = (
    value: string,
    characters: string,
    number: number,
    state: string,
    setter: (newState: string) => void
  ) => {
    let validSpecialCharacterCount = 0;
    let invalidSpecialCharacterCount = 0;
    for (let i = 0; i < value.length; i++) {
      const ch = value.charAt(i);
      if (
        (ch >= 'A' && ch <= 'Z') ||
        (ch >= 'a' && ch <= 'z') ||
        (ch >= '0' && ch <= '9')
      ) {
        continue; // skip alphanumeric characters
      }
      if (characters.indexOf(ch) >= 0) {
        validSpecialCharacterCount++;
      } else {
        invalidSpecialCharacterCount++;
      }
    }
    if (invalidSpecialCharacterCount > 0) {
      if (state !== 'red') {
        setter('red');
      }
    } else if (validSpecialCharacterCount >= number) {
      if (state !== 'green') {
        setter('green');
      }
    } else {
      if (state !== 'red') {
        setter('red');
      }
    }
  };

  /**
   * Validation function for the 1st password input field
   * @param newValue - password
   */
  const onPassword1Changed = (newValue: string) => {
    passwordsComparison(newValue, password2);
    setBackendError('');

    // check length
    if (newValue.length >= passwordPolicy.minLength) {
      if (hasMinLength !== 'green') {
        setHasMinLength('green');
      }
    } else {
      if (hasMinLength !== 'red') {
        setHasMinLength('red');
      }
    }
    // check numbers
    checkNumberOfCharactersByRegex(
      newValue,
      '\\d+',
      passwordPolicy.minNumbers,
      hasMinNumbers,
      setHasMinNumbers
    );
    // minLowerCase
    checkNumberOfCharactersByRegex(
      newValue,
      '[a-z]',
      passwordPolicy.minLowerCase,
      hasMinLowerCase,
      setHasMinLowerCase
    );
    // minUpperCase
    checkNumberOfCharactersByRegex(
      newValue,
      '[A-Z]',
      passwordPolicy.minUpperCase,
      hasMinUpperCase,
      setHasMinUpperCase
    );

    checkNumberOfSpecialCharacters(
      newValue,
      passwordPolicy.availableSpecialCharacters,
      passwordPolicy.minSymbols,
      hasMinSymbols,
      setHasMinSymbols
    );
    setPassword1(newValue);
  };

  function passwordsComparison(password1: string, password2: string) {
    if (password1.length > 0) {
      if (password1 === password2) {
        if (samePasswords !== 'green') {
          setSamePasswords('green');
        }
      } else {
        if (samePasswords !== 'red') {
          setSamePasswords('red');
        }
      }
    }
  }

  /**
   * Validation function for the 2nd password input field
   * @param newValue - password
   */
  const onPassword2Changed = (newValue: string) => {
    passwordsComparison(password1, newValue);
    setPassword2(newValue);
  };

  function handleSave(e: { preventDefault: () => void }) {
    e.preventDefault();
    CommunicationUtil.sendChangeUserPasswordRequest(
      password1,
      () => onNextPage(),
      message => setBackendError(message),
      (pending: boolean) => setRequestPending(pending)
    );
  }

  type PasswordConstraint = {
    nrOfChars?: number;
    state: string;
    text: string;
  };
  const passwordConstraintsOnField1: PasswordConstraint[] = [
    {
      nrOfChars: passwordPolicy.minLength,
      state: hasMinLength,
      text: intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.passwordLengthRule',
        passwordPolicy.minLength
      )
    },
    {
      nrOfChars: passwordPolicy.minNumbers,
      state: hasMinNumbers,
      text: intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.passwordNumbersRule',
        passwordPolicy.minNumbers
      )
    },
    {
      nrOfChars: passwordPolicy.minSymbols,
      state: hasMinSymbols,
      text:
        intlFmt(
          'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.passwordSpecialCharactersRule',
          passwordPolicy.minSymbols
        ) +
        ' ' +
        passwordPolicy.availableSpecialCharacters
    },
    {
      nrOfChars: passwordPolicy.minLowerCase,
      state: hasMinLowerCase,
      text: intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.passwordLowerCaseLettersRule',
        passwordPolicy.minLowerCase
      )
    },
    {
      nrOfChars: passwordPolicy.minUpperCase,
      state: hasMinUpperCase,
      text: intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.passwordUpperCaseLettersRule',
        passwordPolicy.minUpperCase
      )
    }
  ];
  const passwordConstraintsOnField2: PasswordConstraint[] = [
    {
      nrOfChars: 1, // always active check
      state: samePasswords,
      text: intl(
        'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.passwordsMustMatchRule'
      )
    }
  ];

  function createPasswordConstraintElements(
    pwConstraints: PasswordConstraint[]
  ): JSX.Element[] {
    const enabledPasswordConstraints: JSX.Element[] = [];
    for (const passwordConstraint of pwConstraints) {
      if (passwordConstraint.nrOfChars) {
        enabledPasswordConstraints.push(
          <div className="authentication-content-validation-row font-16 s-font">
            <Icon
              {...getIconName(passwordConstraint.state)}
              className={`authentication-content-validation-icon ${passwordConstraint.state} `}
            />
            <p>{passwordConstraint.text}</p>
          </div>
        );
      }
    }
    return enabledPasswordConstraints;
  }

  function createPasswordConstraintViolationAlert(
    pwConstraints: PasswordConstraint[]
  ): JSX.Element | undefined {
    const violations: JSX.Element[] = [];
    for (const passwordConstraint of pwConstraints) {
      if (passwordConstraint.state === 'red') {
        violations.push(
          <li>
            <p>{passwordConstraint.text}</p>
          </li>
        );
      }
    }
    if (violations.length === 0) {
      return undefined;
    }
    return (
      <ol role="alert" className="visually-hidden-for-screenreaders-only">
        {violations}
      </ol>
    );
  }

  /**
   *  Render function
   */
  return (
    <form onSubmit={handleSave} className="password-form">
      <p className="font-16">
        {intl(
          'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.enterYourPasswordText'
        )}
      </p>
      <TextInput
        debounceWait={1000}
        label={intl(
          'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.enterYourPasswordLabel'
        )}
        value={password1}
        onChange={password => onPassword1Changed(password)}
        type="password"
        allowShowPassword
        autoFocus={true}
        error={backendError && intl(backendError as LanguageProperty)}
      />
      {createPasswordConstraintElements(passwordConstraintsOnField1)}
      <TextInput
        debounceWait={1000}
        label={intl(
          'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.repeatPasswordPlaceholder'
        )}
        className="authentication-content-password-repeat-input"
        value={password2}
        onChange={password => onPassword2Changed(password)}
        type="password"
        allowShowPassword
      />
      {createPasswordConstraintElements(passwordConstraintsOnField2)}
      {createPasswordConstraintViolationAlert([
        ...passwordConstraintsOnField1,
        ...passwordConstraintsOnField2
      ])}
      <div className="content-button-bar">
        <Button
          scale={'medium'}
          text={
            isRequestPending
              ? intl(
                  'patientSelfOnboardingPortlet.authenticationWizard.buttonSaving'
                )
              : intl(
                  'patientSelfOnboardingPortlet.authenticationWizardSetPasswordPage.buttonSavePassword'
                )
          }
          disabled={
            !allPasswordRulesChecked() || isRequestPending || !!backendError
          }
          variant="primary"
          type="submit"
        />
      </div>
    </form>
  );
}

export default AuthenticationWizardSetPasswordPage;
